import * as overlay from '../helpers/overlay';
import * as styles from '../helpers/styles';
import * as nonconcurrentAF from '../helpers/nonconcurrent-raf';
import cssClasses from '../base/css-classes';
import COMPONENT_CSS from './point-label-editor.less';

const LABEL_POSITIONS = ['above', 'below', 'left', 'right'];
const ANNOTATION_CHARS = 50;

const activeEditors = [];

/** Checks if a point label is valid (non-empty array)
 * @param {Boolean} pointLabel - Point label array to test
 * @returns {Boolean} - Whether point label is valid
 */
function isValidPointLabel(pointLabel) {
  return pointLabel instanceof Array && pointLabel.length;
}

class PointLabelEditor {
  /** Creates a new PointLabelEditor instance
   * @param {LineChart} chart - LineChart instance to mount editor to
   */
  constructor(chart) {
    this.chart = chart;
    this.selectedPoint = {};
    this.init();
  }

  /** Initializes editor and registers event listeners
   */
  init() {
    const { chart } = this;
    this.build();

    chart.data.lines.forEach((line, i) => {
      const el = document.createElement('option');
      el.value = i;
      el.textContent = line.title;
      this.lineSelect.appendChild(el);
    });

    const updatePointSelect = () => {
      const line = chart.data.lines[this.lineSelect.value];
      if (!line) return;

      while (this.pointSelect.firstChild) {
        this.pointSelect.removeChild(this.pointSelect.firstChild);
      }

      line.points.forEach((point) => {
        const label = chart.data.labels.find(l => l.x === point.x);
        if (!label) return;

        const el = document.createElement('option');
        el.value = point.x;
        el.textContent = label.text;
        this.pointSelect.appendChild(el);

        // Keep selected point if new line has a point at that x-value
        if ('x' in this.selectedPoint && this.selectedPoint.x === point.x) {
          el.selected = true;
        }
      });
    };

    const updateAnnotationCharCount = () => {
      this.annotationChars.textContent = this.annotation.value.length;
    };

    updatePointSelect();

    this.lineSelect.addEventListener('change', updatePointSelect);
    this.lineSelect.addEventListener('change', this.updateControls.bind(this));
    this.pointSelect.addEventListener('change', this.updateControls.bind(this));
    this.showLabel.addEventListener('change', this.updateChart.bind(this));
    this.labelPosition.addEventListener('change', () => {
      if (this.labelPosition.value === 'manual') {
        this.labelTop = -20;
        this.labelLeft = 0;
      }
      this.updateChart();
    });
    this.showOnTile.addEventListener('change', this.updateChart.bind(this));
    this.annotation.addEventListener('keyup', updateAnnotationCharCount);
    this.annotation.addEventListener('change', () => {
      updateAnnotationCharCount();
      this.updateChart();
    });
    this.closeButton.addEventListener('click', this.close.bind(this));

    this.labelHandle.addEventListener('mousedown', (e) => {
      this.draggingLabel = true;

      const rect = this.labelHandle.getBoundingClientRect();
      this.labelHandleOffset = {
        x: Math.round(e.clientX - rect.left),
        y: Math.round(e.clientY - rect.top),
        width: rect.width,
      };

      document.body.classList.add(cssClasses.no_select);
    });

    chart
      .on('dv.draw', this.reposition.bind(this))
      .on('dv.datachanged', this.close.bind(this))
      .on('dv.destroy', this.destroy.bind(this));

    activeEditors.push(this);
    this.updateControls();
  }

  /** Builds editor DOM
   */
  build() {
    styles.add(COMPONENT_CSS);
    this.container = document.createElement('div');
    const { container } = this;
    container.className = cssClasses.label_editor;
    container.innerHTML = `
      <div class="dv-editor-row">
        <label class="dv-editor-column">
          <p class="dv-smallcaps">Line</p>
          <p><select class="dv-editor-line"></select></p>
        </label>
      </div>

      <div class="dv-editor-row">
        <label class="dv-editor-column">
          <p class="dv-smallcaps">Point</p>
          <p><select class="dv-editor-point"></select></p>
        </label>

        <div class="dv-editor-column">
          <p class="dv-smallcaps">Value</p>
          <p class="dv-editor-point-value"></p>
        </div>
      </div>

      <div class="dv-editor-row">
        <div class="dv-editor-column dv-editor-point-options">
          <p class="dv-smallcaps">Options</p>
          <p>
            <label>
              <input type="checkbox" class="dv-editor-show-label">
              Show label
            </label>

            <select class="dv-editor-label-position">
              <option value="above">above</option>
              <option value="below">below</option>
              <option value="left">on left</option>
              <option value="right">on right</option>
              <option value="manual">(manual)</option>
            </select>
          </p>
          <p>
            <label>
              <input type="checkbox" class="dv-editor-show-on-tile">
              Show on tile
            </label>
          </p>
        </div>
      </div>

      <div class="dv-editor-row dv-editor-annotation-row">
        <label class="dv-editor-column">
          <p class="dv-smallcaps">Annotation</p>
          <textarea class="dv-editor-annotation" maxlength="${ANNOTATION_CHARS}"></textarea>
          <p class="dv-annotation-chars"><span class="dv-annotation-chars-current">0</span> / ${ANNOTATION_CHARS}</p>
        </label>
      </div>

      <button type="button" class="dv-editor-close"></button>
    `;
    overlay.add(container);

    this.lineSelect = container.querySelector('.dv-editor-line');
    this.pointSelect = container.querySelector('.dv-editor-point');
    this.pointValue = container.querySelector('.dv-editor-point-value');
    this.showLabel = container.querySelector('.dv-editor-show-label');
    this.labelPosition = container.querySelector('.dv-editor-label-position');
    this.showOnTile = container.querySelector('.dv-editor-show-on-tile');
    this.annotation = container.querySelector('.dv-editor-annotation');
    this.annotationChars = container.querySelector('.dv-annotation-chars-current');
    this.closeButton = container.querySelector('.dv-editor-close');

    this.selectedPointIndicator = document.createElement('div');
    this.selectedPointIndicator.className = cssClasses.selected_point;
    overlay.add(this.selectedPointIndicator);

    this.labelHandle = document.createElement('div');
    this.labelHandle.className = cssClasses.label_handle;
    overlay.add(this.labelHandle);
  }

  /** Updates label editor controls with current state
  */
  updateControls() {
    const { chart, selectedPoint } = this;
    const { pointLabels } = chart.options;

    const lineIndex = Number(this.lineSelect.value);
    const line = chart.data.lines[lineIndex];
    if (!line) return;

    const point = line.points.find(p => p.x === Number(this.pointSelect.value));
    if (!point) return;

    this.pointValue.textContent = point.y;
    this.selectedPointIndicator.style.backgroundColor = line.color;
    selectedPoint.x = point.x;
    selectedPoint.y = point.y;
    selectedPoint.key = `${lineIndex},${point.x}`;
    const { key } = selectedPoint;

    if (pointLabels && isValidPointLabel(pointLabels[key])) {
      this.showLabel.checked = true;
      this.labelPosition.value = LABEL_POSITIONS.indexOf(pointLabels[key][0]) > -1 ? pointLabels[key][0] : 'manual';
      this.showOnTile.checked = !!pointLabels[key][1];
      this.annotation.value = pointLabels[key][2] || '';
    } else {
      this.showLabel.checked = false;
      this.showOnTile.checked = false;
      this.labelPosition.value = 'above';
      this.annotation.value = '';
    }

    this.annotationChars.textContent = this.annotation.value.length;

    this.reposition();
  }

  /** Updates label editor window position
   */
  reposition() {
    const { chart, selectedPoint } = this;

    const chartRect = chart.el.getBoundingClientRect();
    this.container.style.top = `${window.pageYOffset + chartRect.top}px`;
    this.container.style.left = `${chartRect.left}px`;
    this.container.style.height = `${chartRect.height}px`;

    const plotAreaOffset = chart.dom.plotArea.node.getBoundingClientRect();
    const pointPos = chart.getPointPosition({ x: selectedPoint.x, y: selectedPoint.y });
    selectedPoint.screenX = plotAreaOffset.left + pointPos.left;
    selectedPoint.screenY = window.pageYOffset + plotAreaOffset.top + pointPos.top;
    this.selectedPointIndicator.style.top = `${selectedPoint.screenY}px`;
    this.selectedPointIndicator.style.left = `${selectedPoint.screenX}px`;

    this.updateLabelHandle();
  }

  /** Updates LineChart instance with point label config
  */
  updateChart() {
    const { chart, selectedPoint } = this;
    const { key } = selectedPoint;
    const pointLabels = chart.options.pointLabels || {};
    const labelPosition = this.labelPosition.value === 'manual'
      ? `${this.labelTop} ${this.labelLeft}`
      : this.labelPosition.value;

    if (this.showLabel.checked) {
      pointLabels[key] = pointLabels[key] || [];
      pointLabels[key][0] = labelPosition;
      pointLabels[key][1] = this.showOnTile.checked ? 1 : 0;
      pointLabels[key][2] = this.annotation.value;
    } else {
      delete pointLabels[key];
    }

    chart.options.pointLabels = pointLabels;
    chart.redraw();
    this.updateLabelHandle();
    chart.emit('pointlabelschanged');
  }

  /** Updates label positioning handle
   */
  updateLabelHandle() {
    const { chart } = this;
    const pointLabels = chart.options.pointLabels || {};
    const { key } = this.selectedPoint;
    const label = chart.el.querySelector(`[data-key="${key}"]`);
    if (!label) return;

    const rect = label.getBoundingClientRect();
    this.labelHandle.style.top = `${window.pageYOffset + rect.top}px`;
    this.labelHandle.style.left = `${rect.left}px`;
    this.labelHandle.style.width = `${rect.width}px`;
    this.labelHandle.style.height = `${rect.height}px`;

    if (label.getAttribute('data-position')) {
      const [top, left] = label.getAttribute('data-position').split(' ');
      this.labelLeft = left;
      this.labelTop = top;
    }

    if (isValidPointLabel(pointLabels[key]) && LABEL_POSITIONS.indexOf(pointLabels[key][0]) === -1) {
      this.labelHandle.style.display = 'block';
    } else {
      this.labelHandle.style.display = 'none';
    }
  }

  /** Closes label editor window
   */
  close() {
    this.container.classList.add(cssClasses.label_editor_closing);
    setTimeout(() => this.destroy(), 300);
  }

  /** Removes editor DOM elements
   */
  destroy() {
    overlay.remove(this.container);
    overlay.remove(this.selectedPointIndicator);
    overlay.remove(this.labelHandle);
    this.chart.editor = null;
    this.chart.emit('editorclose');

    activeEditors.splice(activeEditors.indexOf(this), 1);
  }
}

/*
** Global Event Listeners
*/

document.addEventListener('mouseup', () => {
  activeEditors.forEach((editor) => {
    editor.draggingLabel = false;
  });
  document.body.classList.remove(cssClasses.no_select);
});

document.addEventListener('mousemove', (e) => {
  nonconcurrentAF.queue(() => {
    activeEditors.forEach((editor) => {
      if (!editor.draggingLabel) return;

      const left = e.clientX - editor.labelHandleOffset.x;
      const top = (window.pageYOffset + e.clientY) - editor.labelHandleOffset.y;
      editor.labelLeft = left - (editor.selectedPoint.screenX + (editor.labelHandleOffset.width / 2));
      editor.labelTop = top - editor.selectedPoint.screenY;

      editor.labelHandle.style.left = `${left}px`;
      editor.labelHandle.style.top = `${top}px`;
      clearTimeout(editor.labelHandleUpdate);
      editor.labelHandleUpdate = setTimeout(() => editor.updateChart(), 50);
    });
  }, 'editor_drag_label');
});

window.addEventListener('resize', () => {
  nonconcurrentAF.queue(() => {
    activeEditors.forEach(editor => editor.reposition());
  }, 'editor_reposition');
});

export default PointLabelEditor;
